import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaTimes,
  FaBuilding,
  FaTrash,
  FaEdit,
  FaPlus,
  FaUsers,
  FaTicketAlt,
  FaSearch,
  FaEye,
  FaChevronLeft,
  FaInfoCircle,
  FaExclamationCircle,
} from 'react-icons/fa';

import { db } from '../../firebase';
import {
  collection,
  doc,
  deleteDoc,
  query,
  where,
  getDocs
} from 'firebase/firestore';

import styles from '../CSS/DepartamentosModal.module.css';
import DepartamentoFormModal from './DepartamentoFormModal';
import TicketDetailsModal from './TicketDetailsModal';

/**
 * Componente principal para gestionar Departamentos, su creación, edición,
 * detalle, y listado de tickets asociados.
 */
const DepartamentosModal = ({
  user,
  departamentos,
  usuarios,
  adminsDepartamento,
  onClose,
  onUpdateDepartamentos
}) => {
  // -------------------------
  // Estados principales
  // -------------------------
  const [modo, setModo] = useState('listar'); // 'listar', 'crear', 'editar', 'detalle'
  const [selectedDepartamentoId, setSelectedDepartamentoId] = useState(null);
  const [selectedDepartamento, setSelectedDepartamento] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [formModalMode, setFormModalMode] = useState(null); // 'crear' o 'editar'

  // NUEVO: Estados para el modal anidado de detalles de ticket
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicketForModal, setSelectedTicketForModal] = useState(null);

  // Tickets y filtros
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('todos');
  
  // Estado para almacenar los administradores del departamento actual
  const [departmentAdmins, setDepartmentAdmins] = useState([]);

  // -------------------------
  // Paginación
  // -------------------------
  const [currentPage, setCurrentPage] = useState(1);
  const [ticketsPerPage] = useState(10);

  /**
   * Función para obtener el ID formateado del ticket
   */
  const getTicketDisplayId = (ticket) => {
    if (ticket.ticket_id) {
      return ticket.ticket_id;
    }
    // Si no hay ticket_id, crear uno basado en el id del documento
    const shortId = ticket.id ? ticket.id.slice(-6) : '000000';
    return `#${shortId.toUpperCase()}`;
  };

  /**
   * MODIFICADO: Función para manejar clic en el ID del ticket
   */
  const handleTicketClick = (ticket) => {
    console.log('Abriendo detalles del ticket:', ticket.id);
    setSelectedTicketForModal(ticket);
    setShowTicketModal(true);
  };

  /**
   * NUEVO: Función para cerrar el modal de ticket anidado
   */
  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedTicketForModal(null);
  };

  /**
   * Cada vez que cambie la búsqueda o se carguen tickets,
   * se actualiza la lista filtrada y paginada.
   */
  useEffect(() => {
    if (tickets.length > 0) {
      let filtered = [...tickets];

      // Filtrar escalados (no los mostramos en esta vista)
      filtered = filtered.filter(ticket => getEstadoSimplificado(ticket.estado) !== null);

      // Filtro por texto (buscar en título, descripción y ticket ID)
      if (searchTerm.trim() !== '') {
        const searchLower = searchTerm.toLowerCase();
        filtered = filtered.filter(
          (ticket) =>
            ticket.titulo?.toLowerCase().includes(searchLower) ||
            ticket.descripcion?.toLowerCase().includes(searchLower) ||
            getTicketDisplayId(ticket).toLowerCase().includes(searchLower)
        );
      }

      // Filtro por estado
      if (statusFilter !== 'todos') {
        filtered = filtered.filter(ticket => {
          const estado = getEstadoSimplificado(ticket.estado);
          return estado === statusFilter;
        });
      }

      setFilteredTickets(filtered);
      // Resetear a la primera página solo cuando cambia el filtro o la búsqueda
      if (tickets.length && filtered.length !== tickets.length) {
        setCurrentPage(1);
      }
    } else {
      setFilteredTickets([]);
    }
  }, [searchTerm, statusFilter, tickets]);

  // Efecto para cargar los administradores del departamento seleccionado
  useEffect(() => {
    if (selectedDepartamentoId && adminsDepartamento) {
      const admins = adminsDepartamento.filter(
        admin => admin.departamento_id === selectedDepartamentoId
      );
      setDepartmentAdmins(admins);
    } else {
      setDepartmentAdmins([]);
    }
  }, [selectedDepartamentoId, adminsDepartamento]);
  useEffect(() => {
    // Actualizar selectedDepartamento cuando cambie en la lista de departamentos
    if (selectedDepartamentoId && departamentos) {
      const departamentoActualizado = departamentos.find(d => d.id === selectedDepartamentoId);
      if (departamentoActualizado) {
        setSelectedDepartamento(departamentoActualizado);
      }
    }
  }, [departamentos, selectedDepartamentoId]);

  // Obtener tickets para la página actual
  const indexOfLastTicket = currentPage * ticketsPerPage;
  const indexOfFirstTicket = indexOfLastTicket - ticketsPerPage;
  const currentTickets = filteredTickets.slice(indexOfFirstTicket, indexOfLastTicket);
  const totalPages = Math.ceil(filteredTickets.length / ticketsPerPage);

  // Cambiar de página de forma segura
  const paginate = (pageNumber) => {
    const validPage = Math.max(1, Math.min(pageNumber, totalPages || 1));
    setCurrentPage(validPage);
  };

  // CORREGIDO - Clasificación de tickets por estado (sin mostrar escalados)
  const getEstadoSimplificado = (estadoOriginal) => {
    switch (estadoOriginal) {
      case 'nuevo':
      case 'abierto':
        return 'nuevo';
      case 'en_proceso':
      case 'en_progreso':
        return 'en_proceso';
      case 'escalado':
        return null; // No mostrar escalados en esta vista
      case 'cerrado':
      case 'resuelto':
        return 'cerrado';
      default:
        return 'nuevo'; // Por defecto
    }
  };

  // CORREGIDO - Helper para obtener nombre de usuario (priorizar campos de nombre directo)
  const getUserName = (ticket, field) => {
    // Primero intentar usar los campos de nombre directo (como en TicketDetailsModal)
    if (field === 'solicitante') {
      if (ticket.created_by_name) return ticket.created_by_name;
      if (ticket.solicitante_nombre) return ticket.solicitante_nombre;
      
      // Si no hay nombre directo, buscar por ID
      const userId = ticket.created_by || ticket.solicitante_id;
      if (userId) {
        const userObj = usuarios?.find((u) => u.id === userId);
        return userObj ? userObj.displayName || userObj.email : 'Usuario desconocido';
      }
    }
    
    if (field === 'asignado') {
      if (ticket.assigned_to_name) return ticket.assigned_to_name;
      if (ticket.asignado_nombre) return ticket.asignado_nombre;
      
      // Si no hay nombre directo, buscar por ID
      const userId = ticket.assigned_to || ticket.asignado_id;
      if (userId) {
        const userObj = usuarios?.find((u) => u.id === userId);
        return userObj ? userObj.displayName || userObj.email : 'Sin asignar';
      }
    }
    
    return field === 'asignado' ? 'Sin asignar' : 'Usuario desconocido';
  };

  // CORREGIDO - Función para formatear fecha (compatible con TicketDetailsModal)
  const formatDate = (date) => {
    if (!date) return '-';
    
    try {
      let dateObj;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string') {
        dateObj = new Date(date);
      } else if (date.seconds) {
        // Timestamp de Firestore
        dateObj = new Date(date.seconds * 1000);
      } else {
        dateObj = new Date(date);
      }
      
      if (isNaN(dateObj.getTime())) {
        return '-';
      }
      
      return dateObj.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  // -------------------------
  // Handlers: Crear / Editar / Eliminar / Ver Detalle
  // -------------------------
  const handleCreateDepartamento = () => {
    setFormModalMode('crear');
    setShowFormModal(true);
  };

  const handleEditDepartamento = useCallback((depId) => {
    const departamento = departamentos.find((d) => d.id === depId);
    if (departamento) {
      setSelectedDepartamentoId(depId);
      setSelectedDepartamento(departamento);
      setFormModalMode('editar');
      setShowFormModal(true);
    }
  }, [departamentos]);

  const handleDeleteDepartamento = useCallback(async (depId) => {
    const departamento = departamentos.find((d) => d.id === depId);
    const nombreDepartamento = departamento?.nombre || 'este departamento';
  
    try {
      // Primero contar cuántos tickets tiene el departamento
      const ticketsRef = collection(db, 'tickets');
      const q = query(ticketsRef, where('departamento_id', '==', depId));
      const querySnapshot = await getDocs(q);
      const numTickets = querySnapshot.size;
  
      // Mensaje de confirmación personalizado según la cantidad de tickets
      let confirmMessage;
      if (numTickets === 0) {
        confirmMessage = `¿Estás seguro de que deseas eliminar el departamento "${nombreDepartamento}"?\n\nEsta acción no se puede deshacer.`;
      } else {
        confirmMessage = 
          `¿Estás seguro de que deseas eliminar el departamento "${nombreDepartamento}"?\n\n` +
          `⚠️ ADVERTENCIA: Este departamento tiene ${numTickets} ticket${numTickets === 1 ? '' : 's'} asociado${numTickets === 1 ? '' : 's'}.\n` +
          `Todos los tickets también serán eliminados permanentemente.\n\n` +
          `Esta acción no se puede deshacer.`;
      }
  
      if (window.confirm(confirmMessage)) {
        // Eliminar todos los tickets asociados
        const deletePromises = [];
        querySnapshot.forEach((ticketDoc) => {
          deletePromises.push(deleteDoc(doc(db, 'tickets', ticketDoc.id)));
        });
  
        // Ejecutar eliminaciones en paralelo
        await Promise.all(deletePromises);
  
        // Eliminar el departamento
        await deleteDoc(doc(db, 'departamentos', depId));
  
        // Actualizar estado local
        onUpdateDepartamentos((prev) => prev.filter((dep) => dep.id !== depId));
  
        // Si estamos viendo este departamento, volver a la lista
        if (modo === 'detalle' && selectedDepartamentoId === depId) {
          setModo('listar');
        }
  
        // Mensaje de confirmación
        if (numTickets > 0) {
          alert(
            `✅ Departamento "${nombreDepartamento}" eliminado exitosamente.\n` +
            `También se eliminaron ${numTickets} ticket${numTickets === 1 ? '' : 's'} asociado${numTickets === 1 ? '' : 's'}.`
          );
        } else {
          alert(`✅ Departamento "${nombreDepartamento}" eliminado exitosamente.`);
        }
      }
  
    } catch (error) {
      console.error('Error eliminando departamento y tickets:', error);
      alert(
        '❌ Error al eliminar el departamento.\n\n' +
        'Es posible que algunos elementos no se hayan eliminado correctamente. ' +
        'Por favor, verifica e inténtalo de nuevo.'
      );
    }
  }, [modo, selectedDepartamentoId, onUpdateDepartamentos, departamentos]);

  const handleViewDepartamento = useCallback(async (depId) => {
    setIsLoading(true);
    const departamento = departamentos.find((d) => d.id === depId);

    if (departamento) {
      setSelectedDepartamento(departamento);
      setSelectedDepartamentoId(depId);

      try {
        const ticketsRef = collection(db, 'tickets');
        const q = query(ticketsRef, where('departamento_id', '==', depId));
        const querySnapshot = await getDocs(q);

        const ticketsData = [];
        querySnapshot.forEach((docTicket) =>
          ticketsData.push({ id: docTicket.id, ...docTicket.data() })
        );

        console.log('Tickets cargados:', ticketsData); // Debug

        setTickets(ticketsData);
        setFilteredTickets(ticketsData);
        setCurrentPage(1);
        setStatusFilter('todos');
        setSearchTerm('');
        setModo('detalle');
      } catch (error) {
        console.error('Error al cargar tickets:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [departamentos]);

  const handleCloseFormModal = () => {
    setShowFormModal(false);
    setFormModalMode(null);
  };

  // -------------------------
  // Sub-componentes
  // -------------------------

  /**
   * Sub-componente: Listado de departamentos (vista vertical)
   */
  const ListView = () => {
    return (
      <div className={styles.departamentosListContainer}>
        <div className={styles.departamentosListHeader}>
          <div className={styles.headerLeft}>
            <h3 className={styles.sectionTitle}>
              <FaBuilding className={styles.sectionIcon} />
              Departamentos
              <span className={styles.departmentCount}>
                {departamentos.length}
              </span>
            </h3>
          </div>

          <div className={styles.headerActions}>
            <button
              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnCreate}`}
              onClick={handleCreateDepartamento}
            >
              <FaPlus />
              <span>Nuevo</span>
            </button>
          </div>
        </div>

        {departamentos.length === 0 ? (
          <div className={styles.emptyState}>
            <FaBuilding className={styles.emptyStateIcon} />
            <p>No hay departamentos registrados en el sistema.</p>
            <button
              className={`${styles.btn} ${styles.btnPrimary} ${styles.btnEmptyState}`}
              onClick={handleCreateDepartamento}
            >
              <FaPlus />
              Crear Departamento
            </button>
          </div>
        ) : (
          <div className={styles.departamentosGrid}>
            {departamentos.map((dep) => (
              <div key={dep.id} className={styles.departamentoCard}>
                <div className={styles.departamentoInfo}>
                  <h4 className={styles.departamentoNombre}>{dep.nombre}</h4>
                </div>

                <div className={styles.departamentoMetadata}>
                  <div className={styles.metadataItem}>
                    <FaUsers className={styles.metadataIcon} />
                    <span>
                      {adminsDepartamento
                        ? adminsDepartamento.filter(admin => admin.departamento_id === dep.id).length
                        : 0}
                    </span>
                  </div>
                </div>

                <div className={styles.departamentoActions}>
                  <button
                    className={`${styles.actionIconButton} ${styles.viewButton}`}
                    onClick={() => handleViewDepartamento(dep.id)}
                    title="Ver detalles"
                  >
                    <FaEye />
                  </button>
                  <button
                    className={`${styles.actionIconButton} ${styles.editButton}`}
                    onClick={() => handleEditDepartamento(dep.id)}
                    title="Editar"
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`${styles.actionIconButton} ${styles.deleteButton}`}
                    onClick={() => handleDeleteDepartamento(dep.id)}
                    title="Eliminar"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  /**
   * Sub-componente: Vista de detalle (tickets, información, administradores)
   */
  const DetailView = () => {
    if (!selectedDepartamento) return null;

    // CORREGIDO - Renderiza estadísticas de tickets con estados correctos (sin escalados)
    const renderTicketStats = () => {
      if (!tickets || tickets.length === 0) return null;

      const nuevos = tickets.filter(t => getEstadoSimplificado(t.estado) === 'nuevo').length;
      const enProceso = tickets.filter(t => getEstadoSimplificado(t.estado) === 'en_proceso').length;
      const cerrados = tickets.filter(t => getEstadoSimplificado(t.estado) === 'cerrado').length;

      return (
        <div className={styles.ticketStats}>
          <div className={styles.statItem}>
            <p className={`${styles.statNumber} ${styles.nuevosTickets}`}>{nuevos}</p>
            <p className={styles.statLabel}>Nuevos</p>
          </div>
          <div className={styles.statItem}>
            <p className={`${styles.statNumber} ${styles.enProgresoTickets}`}>{enProceso}</p>
            <p className={styles.statLabel}>En Proceso</p>
          </div>
          <div className={styles.statItem}>
            <p className={`${styles.statNumber} ${styles.cerradosTickets}`}>{cerrados}</p>
            <p className={styles.statLabel}>Cerrados</p>
          </div>
        </div>
      );
    };

    return (
      <div className={styles.detailContainer}>
        <div className={styles.detailHeader}>
          <div className={styles.backButtonContainer}>
            <button
              className={`${styles.btn} ${styles.btnSecondary} ${styles.btnBack}`}
              onClick={() => setModo('listar')}
            >
              <FaChevronLeft />
              <span>Volver</span>
            </button>
          </div>

          <div className={styles.detailTitleContainer}>
            <h2 className={styles.detailTitle}>{selectedDepartamento.nombre}</h2>
          </div>

          <div className={styles.departamentoActions}>
            <button
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={() => handleEditDepartamento(selectedDepartamentoId)}
            >
              <FaEdit />
              <span>Editar</span>
            </button>
            <button
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={() => handleDeleteDepartamento(selectedDepartamentoId)}
            >
              <FaTrash />
              <span>Eliminar</span>
            </button>
          </div>
        </div>

        <div className={styles.detailGrid}>
          <div className={styles.detailMainContent}>
            {selectedDepartamento.descripcion && (
              <div className={styles.detailCard}>
                <div className={styles.detailCardHeader}>
                  <FaInfoCircle className={styles.detailCardIcon} />
                  <h3 className={styles.detailCardTitle}>Descripción</h3>
                </div>
                <div className={styles.detailCardContent}>
                  <p className={styles.descriptionText}>
                    {selectedDepartamento.descripcion.length > 300 
                      ? `${selectedDepartamento.descripcion.substring(0, 300)}...`
                      : selectedDepartamento.descripcion
                    }
                  </p>
                  {selectedDepartamento.descripcion.length > 300 && (
                    <span className={styles.descriptionTruncated}>
                      Descripción truncada. Total: {selectedDepartamento.descripcion.length} caracteres.
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className={styles.detailCard}>
              <div className={styles.detailCardHeader}>
                <FaTicketAlt className={styles.detailCardIcon} />
                <h3 className={styles.detailCardTitle}>Tickets</h3>
              </div>
              <div className={styles.detailCardContent}>
                {renderTicketStats()}

                {tickets.length > 0 && (
                  <div className={styles.filterControls}>
                    <div className={styles.searchBox}>
                      <input
                        type="text"
                        placeholder="Buscar ticket..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={styles.searchInput}
                      />
                      <FaSearch className={styles.searchIcon} />
                    </div>

                    <div className={styles.statusFilterContainer}>
                      <button
                        className={`${styles.statusFilterPill} ${statusFilter === 'todos' ? styles.active : ''}`}
                        onClick={() => setStatusFilter('todos')}
                      >
                        Todos ({tickets.length})
                      </button>
                      <button
                        className={`${styles.statusFilterPill} ${statusFilter === 'nuevo' ? styles.active : ''}`}
                        onClick={() => setStatusFilter('nuevo')}
                      >
                        Nuevos ({tickets.filter(t => getEstadoSimplificado(t.estado) === 'nuevo').length})
                      </button>
                      <button
                        className={`${styles.statusFilterPill} ${statusFilter === 'en_proceso' ? styles.active : ''}`}
                        onClick={() => setStatusFilter('en_proceso')}
                      >
                        En proceso ({tickets.filter(t => getEstadoSimplificado(t.estado) === 'en_proceso').length})
                      </button>
                      <button
                        className={`${styles.statusFilterPill} ${statusFilter === 'cerrado' ? styles.active : ''}`}
                        onClick={() => setStatusFilter('cerrado')}
                      >
                        Cerrados ({tickets.filter(t => getEstadoSimplificado(t.estado) === 'cerrado').length})
                      </button>
                    </div>
                  </div>
                )}

                {isLoading ? (
                  <div className={styles.loadingContainer}>
                    <div className={styles.spinner}></div>
                    <p>Cargando tickets...</p>
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className={styles.emptyState}>
                    <FaTicketAlt className={styles.emptyStateIcon} />
                    <p>No se encontraron tickets para este departamento.</p>
                  </div>
                ) : (
                  <div className={styles.tableContainer}>
                    <table className={styles.ticketsTable}>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Estado</th>
                          <th>Solicitante</th>
                          <th>Asignado a</th>
                          <th>Fecha</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTickets.map((ticket) => {
                          const estadoSimplificado = getEstadoSimplificado(ticket.estado);
                          const ticketDisplayId = getTicketDisplayId(ticket);
                          
                          return (
                            <tr
                              key={ticket.id}
                              className={`${styles.ticketRow} ${styles.clickableRow} ${
                                estadoSimplificado === 'nuevo'
                                  ? styles.rowNuevo
                                  : estadoSimplificado === 'en_proceso'
                                  ? styles.rowEnProgreso
                                  : styles.rowCerrado
                              }`}
                              onClick={() => handleTicketClick(ticket)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td 
                                className={styles.idCell} 
                                data-label="ID"
                              >
                                <button 
                                  className={styles.ticketIdButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTicketClick(ticket);
                                  }}
                                >
                                  {ticketDisplayId}
                                </button>
                              </td>
                              <td data-label="Estado">
                                <span className={`${styles.statusBadge} ${styles[`status-${estadoSimplificado}`]}`}>
                                  {estadoSimplificado === 'nuevo' ? 'Nuevo' :
                                  estadoSimplificado === 'en_proceso' ? 'En Proceso' : 'Cerrado'}
                                </span>
                              </td>
                              <td data-label="Solicitante">
                                {getUserName(ticket, 'solicitante')}
                              </td>
                              <td data-label="Asignado a">
                                {getUserName(ticket, 'asignado')}
                              </td>
                              <td data-label="Fecha">
                                {formatDate(ticket.created_at)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination Controls - Solo mostrar si hay más de una página */}
                {filteredTickets.length > ticketsPerPage && (
                  <div className={styles.pagination}>
                    <button
                      className={styles.pageButton}
                      onClick={() => paginate(1)}
                      disabled={currentPage === 1}
                      aria-label="Primera página"
                    >
                      &laquo;
                    </button>
                    <button
                      className={styles.pageButton}
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      aria-label="Página anterior"
                    >
                      &lsaquo;
                    </button>
                    <span className={styles.pageInfo}>
                      Página {currentPage} de {totalPages}
                    </span>
                    <button
                      className={styles.pageButton}
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      aria-label="Página siguiente"
                    >
                      &rsaquo;
                    </button>
                    <button
                      className={styles.pageButton}
                      onClick={() => paginate(totalPages)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      aria-label="Última página"
                    >
                      &raquo;
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.detailSidebar}>
            <div className={styles.detailCard}>
              <div className={styles.detailCardHeader}>
                <FaInfoCircle className={styles.detailCardIcon} />
                <h3 className={styles.detailCardTitle}>Información</h3>
              </div>
              <div className={styles.detailCardContent}>
                <div className={styles.sidebarItem}>
                  <span className={styles.sidebarLabel}>Creado:</span>
                  <span className={styles.sidebarValue}>
                    {formatDate(selectedDepartamento.created_at)}
                  </span>
                </div>
                {selectedDepartamento.updated_at && (
                  <div className={styles.sidebarItem}>
                    <span className={styles.sidebarLabel}>Actualizado:</span>
                    <span className={styles.sidebarValue}>
                      {formatDate(selectedDepartamento.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailCardHeader}>
                <FaUsers className={styles.detailCardIcon} />
                <h3 className={styles.detailCardTitle}>Administradores</h3>
              </div>
              <div className={styles.detailCardContent}>
                {departmentAdmins && departmentAdmins.length > 0 ? (
                  <div className={styles.adminsList}>
                    {departmentAdmins.map((admin) => (
                      <div key={admin.id} className={styles.adminItem}>
                        <div className={styles.adminAvatar}>
                          {admin.nombre.charAt(0).toUpperCase()}
                        </div>
                        <span className={styles.adminName} title={admin.email}>
                          {admin.nombre}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noAdmins}>
                    <p>No hay administradores asignados a este departamento.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // -------------------------
  // Animaciones (framer-motion)
  // -------------------------
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };

  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  // -------------------------
  // Render principal
  // -------------------------
  return (
    <>
      <motion.div
        className={`${styles.modalOverlay} ${showTicketModal ? styles.nestedParent : ''}`}
        initial="hidden"
        animate="visible"
        exit="hidden"
        variants={overlayVariants}
        style={{
          zIndex: showTicketModal ? 1040 : 1050
        }}
      >
        <motion.div
          className={styles.modalContent}
          variants={modalVariants}
          animate={{
            filter: showTicketModal ? 'blur(3px)' : 'blur(0px)',
            scale: showTicketModal ? 0.95 : 1,
            transition: { duration: 0.3 }
          }}
          style={{
            maxWidth:
              modo === 'detalle'
                ? '1100px'
                : modo === 'listar'
                ? '1000px'
                : '700px',
            pointerEvents: showTicketModal ? 'none' : 'auto'
          }}
        >
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}>
              <FaBuilding className={styles.titleIcon} />
              {modo === 'listar'
                ? 'Gestionar Departamentos'
                : modo === 'detalle'
                ? `Departamento: ${selectedDepartamento?.nombre || ''}`
                : ''}
            </h2>
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Cerrar modal"
            >
              <FaTimes />
            </button>
          </div>
          <div className={styles.modalBody}>
            {modo === 'listar' && <ListView />}
            {modo === 'detalle' && <DetailView />}
          </div>
        </motion.div>
      </motion.div>

      {/* Modal para Crear/Editar departamento */}
      <AnimatePresence>
        {showFormModal && (
          <DepartamentoFormModal
            user={user}
            modo={formModalMode}
            departamento={formModalMode === 'editar' ? selectedDepartamento : null}
            onClose={handleCloseFormModal}
            onUpdateDepartamentos={onUpdateDepartamentos}
          />
        )}
      </AnimatePresence>

      {/* NUEVO: Modal anidado para detalles de ticket con animación */}
      <AnimatePresence>
        {showTicketModal && selectedTicketForModal && (
          <motion.div 
            className={styles.nestedModalOverlay}
            initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            animate={{ opacity: 1, backdropFilter: 'blur(6px)' }}
            exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
            transition={{ duration: 0.3 }}
            onClick={(e) => {
              // Cerrar modal si se hace clic en el overlay
              if (e.target === e.currentTarget) {
                handleCloseTicketModal();
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()} // Prevenir cierre al hacer clic dentro del modal
            >
              <TicketDetailsModal
                ticket={selectedTicketForModal}
                mode="ver"
                user={user}  
                userRole="admin"
                departamentos={departamentos}
                adminsDepartamento={adminsDepartamento}
                onClose={handleCloseTicketModal}
                onResponder={() => {
                  console.log('Responder ticket desde modal anidado');
                  // Aquí podrías abrir otro modal para responder
                  // o manejar la respuesta directamente
                }}
                onAsignar={(ticketId, adminId) => {
                  console.log('Asignar ticket desde modal anidado', ticketId, adminId);
                  // Manejar asignación si es necesario
                }}
                onCerrar={() => {
                  console.log('Cerrar ticket desde modal anidado');
                  // Manejar cierre si es necesario
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default DepartamentosModal;